import type { ChecklistRequirement, RequirementStatus } from "@recobra/schemas/task";

type NonEmptyArray<T> = [T, ...T[]];

/** Describes a set of requirements that collapse into one group once they have enough rows. */
export interface ChecklistGroupSpec {
  /** Stable identity of the group among a checklist's rows. */
  key: string;
  includes: (requirement: ChecklistRequirement) => boolean;
  /** Below this count, the matching rows render individually instead of grouping. */
  minItems: number;
}

export type ChecklistRowEntry<Spec extends ChecklistGroupSpec> =
  | { type: "item"; status: RequirementStatus }
  | { type: "group"; spec: Spec; statuses: NonEmptyArray<RequirementStatus> };

/**
 * Collapses the rows each spec includes into one group entry (at the position
 * of its first occurrence) once together they have at least `minItems` rows;
 * every other row, and any spec below its own threshold, passes through
 * individually. A row belongs to the first spec that includes it. Adding a new
 * group is one entry in `specs`, not a new branch here.
 */
export const buildChecklistRows = <Spec extends ChecklistGroupSpec>(
  statuses: readonly RequirementStatus[],
  specs: readonly Spec[],
): ChecklistRowEntry<Spec>[] => {
  const specFor = (status: RequirementStatus) =>
    specs.find((spec) => spec.includes(status.requirement));

  const statusesBySpec = new Map<Spec, RequirementStatus[]>();
  for (const status of statuses) {
    const spec = specFor(status);
    if (!spec) continue;
    const list = statusesBySpec.get(spec);
    if (list) list.push(status);
    else statusesBySpec.set(spec, [status]);
  }

  const renderedGroupSpecs = new Set<Spec>();

  return statuses.flatMap((status): ChecklistRowEntry<Spec>[] => {
    const spec = specFor(status);
    const groupStatuses = spec && statusesBySpec.get(spec);

    if (!spec || !groupStatuses || groupStatuses.length < spec.minItems) {
      return [{ type: "item", status }];
    }
    if (renderedGroupSpecs.has(spec)) return [];

    renderedGroupSpecs.add(spec);
    return [{ type: "group", spec, statuses: asNonEmpty(groupStatuses) }];
  });
};

const asNonEmpty = <T>(items: T[]): NonEmptyArray<T> => {
  const [first, ...rest] = items;
  if (items.length === 0) throw new Error("expected a non-empty array of grouped rows");
  return [first as T, ...rest];
};
